<template>
  <div class="dashboard-bi d-flex flex-column vh-100">
    <header class="dashboard-header d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
      <div>
        <h5 class="mb-0 fw-bold">
          <i class="bi bi-graph-up-arrow me-2 dashboard-icon"></i>BI Saldo Estoque - Especiais
        </h5>
        <small class="dashboard-subtitulo">{{ subtituloHeader }}</small>
      </div>
      <div class="d-flex align-items-center gap-2">
        <PresetFiltrosSalvos :filtros-atuais="filtros" @carregar-preset="carregarPreset" />
        <button class="btn btn-outline-secondary btn-sm" @click="exportarGrid" title="Exportar CSV/XLSX">
          <i class="bi bi-download me-1"></i> Exportar
        </button>
        <button class="btn btn-outline-secondary btn-sm" @click="abrirFiltrosAvancados = true" title="Filtros Avançados">
          <i class="bi bi-sliders me-1"></i> Filtros Avançados
        </button>
        <button class="btn btn-outline-danger btn-sm" @click="resetarTudo" title="Limpar todos os filtros">
          <i class="bi bi-arrow-counterclockwise"></i>
        </button>
      </div>
    </header>

    <GavetaFiltrosAvancados :aberta="abrirFiltrosAvancados" :filtros="filtrosAvancados" @fechar="abrirFiltrosAvancados = false" @aplicar="aplicarFiltrosAvancados" />

    <div class="dashboard-corpo d-flex flex-grow-1 overflow-hidden">
      <div class="dashboard-sidebar border-end" :class="{ 'd-none': !sidebarVisivel }">
        <SidebarFiltrosBI
          :slicers-config="slicersConfig"
          :filtros="filtros"
          :opcoes-por-dimensao="opcoesPorDimensao"
          :loading-states="loadingSlicers"
          @update:filtros="onFiltrosChange"
          @abrir-avancados="abrirFiltrosAvancados = true"
        />
      </div>

      <div class="dashboard-main d-flex flex-column flex-grow-1 overflow-hidden">
        <BarraFiltrosAtivos :badges="badgesAtivos" :subtitulo="subtituloHeader" @remover-filtro="removerBadge" @limpar-todos="resetarTudo" />

        <div class="dashboard-kpis px-3 py-2 border-bottom">
          <div class="d-flex gap-2 flex-wrap">
            <KpiCardAnalitico
              v-for="kpi in kpisConfig"
              :key="kpi.chave"
              :rotulo="kpi.rotulo"
              :icone="kpi.icone"
              :valor="kpi.valor"
              :formato="kpi.formato"
              :badge="kpi.badge"
              :clicavel="kpi.clicavel"
              :ativo="kpiAtivo === kpi.chave"
              :loading="loadingResumo"
              @click="kpi.clicavel && ordenarPorKPI(kpi.chave)"
            />
          </div>
        </div>

        <div class="dashboard-grid flex-grow-1 overflow-auto p-0">
          <AdminTable
            ref="adminTableRef"
            :columns="colunasGrid"
            :data="gridData"
            :loading="loadingGrid"
            :total-pages="totalPages"
            :current-page="currentPage"
            @page-change="onPageChange"
            @sort-change="onSortChange"
          />
        </div>
      </div>
    </div>

    <button
      class="btn btn-sm btn-primary position-fixed d-md-none"
      style="bottom: 16px; left: 16px; z-index: 1050; border-radius: 50%; width: 44px; height: 44px;"
      @click="sidebarVisivel = !sidebarVisivel"
    >
      <i class="bi bi-funnel"></i>
    </button>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import api from '@/services/api'
import SidebarFiltrosBI from '@/components/base/bi/SidebarFiltrosBI.vue'
import BarraFiltrosAtivos from '@/components/base/bi/BarraFiltrosAtivos.vue'
import KpiCardAnalitico from '@/components/base/bi/KpiCardAnalitico.vue'
import PresetFiltrosSalvos from '@/components/base/bi/PresetFiltrosSalvos.vue'
import GavetaFiltrosAvancados from '@/components/base/bi/GavetaFiltrosAvancados.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'

const DIMENSOES = [
  { chave: 'embarque', titulo: 'Embarques', icone: 'bi bi-truck' },
  { chave: 'contrato', titulo: 'Contratos', icone: 'bi bi-file-text' },
  { chave: 'fornecedor', titulo: 'Fornecedores de Contrato (Importação)', icone: 'bi bi-building' },
  { chave: 'produto', titulo: 'Produtos', icone: 'bi bi-box-seam' },
  { chave: 'filial', titulo: 'Filiais', icone: 'bi bi-building' },
  { chave: 'localEstoque', titulo: 'Locais Estoque', icone: 'bi bi-geo-alt' },
  { chave: 'tipoLocal', titulo: 'Tipo Local', icone: 'bi bi-tags' },
  { chave: 'produtor', titulo: 'Produtores', icone: 'bi bi-person-badge' },
  { chave: 'lote', titulo: 'Lotes', icone: 'bi bi-upc-scan' }
]

const sidebarVisivel = ref(true)
const slicersConfig = DIMENSOES.map(d => ({ ...d, maxVisiveis: 5 }))
const filtros = ref({})
const filtrosAvancados = ref({})
const abrirFiltrosAvancados = ref(false)
const opcoesPorDimensao = ref({})
const loadingSlicers = ref({})
const resumo = ref(null)
const loadingResumo = ref(false)
const kpiAtivo = ref(null)
const gridData = ref([])
const loadingGrid = ref(false)
const currentPage = ref(1)
const totalPages = ref(1)
const sortBy = ref(null)
const sortOrder = ref('asc')
const adminTableRef = ref(null)
let debounceTimer = null

const subtituloHeader = computed(() => {
  const totalFiltros = Object.values(filtros.value).reduce((acc, arr) => acc + (Array.isArray(arr) ? arr.length : 0), 0)
  const totalItens = resumo.value?.totalItens || 0
  const custoMedio = resumo.value?.custoMedioPadrao
  let texto = `${totalFiltros} filtro${totalFiltros !== 1 ? 's' : ''} ativo${totalFiltros !== 1 ? 's' : ''}`
  texto += ` • ${totalItens.toLocaleString('pt-BR')} itens`
  if (custoMedio) texto += ` • Custo Médio: R$ ${Number(custoMedio).toFixed(2)}`
  return texto
})

const badgesAtivos = computed(() => {
  const badges = []
  for (const [chave, ids] of Object.entries(filtros.value)) {
    if (!Array.isArray(ids) || ids.length === 0) continue
    const config = DIMENSOES.find(d => d.chave === chave)
    const opcoes = opcoesPorDimensao.value[chave] || []
    for (const id of ids) {
      const opcao = opcoes.find(o => o.id === id)
      badges.push({
        chave,
        id,
        rotulo: opcao?.rotulo || `${config?.titulo || chave}: #${id}`,
        icone: config?.icone || 'bi bi-funnel',
        tooltip: opcao?.tooltip || ''
      })
    }
  }
  return badges
})

const kpisConfig = computed(() => {
  const r = resumo.value || {}
  return [
    { chave: 'volumes', rotulo: 'Volumes', icone: 'bi bi-boxes', valor: r.totalVolumesEstoque || 0, formato: 'compacto', badge: 'CW', clicavel: false },
    { chave: 'custoMedio', rotulo: 'Custo Médio', icone: 'bi bi-cash-coin', valor: r.custoMedioPadrao || 0, formato: 'moeda', badge: null, clicavel: true },
    { chave: 'valorBRL', rotulo: 'Total BRL', icone: 'bi bi-currency-dollar', valor: r.totalValorPadrao || 0, formato: 'moeda', badge: null, clicavel: true },
    { chave: 'valorUSD', rotulo: 'Total USD', icone: 'bi bi-currency-exchange', valor: r.totalValorGerencial || 0, formato: 'moeda-usd', badge: null, clicavel: true },
    { chave: 'itens', rotulo: 'Itens', icone: 'bi bi-layers', valor: r.totalItens || 0, formato: 'compacto', badge: null, clicavel: false }
  ]
})

const colunasGrid = [
  { field: 'skuProduto', label: 'SKU', width: 90 },
  { field: 'nomeProduto', label: 'Produto', width: null, flex: 2 },
  { field: 'numeroContratoFornecedor', label: 'Contrato', width: 110 },
  { field: 'idEmbarque', label: 'Embarque #', width: 80 },
  { field: 'nomeFilial', label: 'Filial', width: 100 },
  { field: 'nomeLocalEstoque', label: 'Local Estoque', width: 120 },
  { field: 'quantidadeEmEstoque', label: 'Qtd Estoque', width: 90, align: 'right' },
  { field: 'quantidadeDisponivel', label: 'Qtd Disponível', width: 90, align: 'right' },
  { field: 'quantidadeReservada', label: 'Qtd Reservada', width: 90, align: 'right' },
  { field: 'quantidadeVolumesEmEstoque', label: 'Volumes', width: 70, align: 'right' },
  { field: 'custoUnitOrigemPadrao', label: 'Custo BRL', width: 110, align: 'right' },
  { field: 'custoUnitOrigemGerencial', label: 'Custo USD', width: 110, align: 'right' }
]

onMounted(() => carregarTudo())

function montarQueryString () {
  const params = new URLSearchParams()
  for (const [chave, ids] of Object.entries(filtros.value)) {
    if (Array.isArray(ids) && ids.length > 0) params.append(mapearParametro(chave), ids.join(','))
  }
  if (filtrosAvancados.value.numeroDocumentoEntrada) params.append('numeroDocumentoEntrada', filtrosAvancados.value.numeroDocumentoEntrada)
  if (filtrosAvancados.value.numeroDocumentoTransporte) params.append('numeroDocumentoTransporte', filtrosAvancados.value.numeroDocumentoTransporte)
  if (filtrosAvancados.value.ocultarSaldoZero) params.append('ocultarSaldoZero', 'true')
  if (filtrosAvancados.value.apenasDisponivel) params.append('apenasDisponivel', 'true')
  return params.toString()
}

function mapearParametro (chave) {
  const mapa = {
    embarque: 'idsEmbarquesFiltro',
    contrato: 'idsContratos',
    localEstoque: 'idsLocaisEstoque',
    fornecedor: 'idsFornecedores',
    produto: 'idsProdutos',
    produtor: 'idsProdutoresFiltro',
    filial: 'idsFiliais',
    tipoLocal: 'lstTipoLocal',
    lote: 'idsLotes'
  }
  return mapa[chave] || chave
}

async function carregarTudo () {
  await Promise.all([carregarResumo(), carregarGrid(), carregarOpcoesDimensao()])
}

async function carregarResumo () {
  loadingResumo.value = true
  try {
    const { data } = await api.get(`/SaldosEstoqueOrigem/resumo?${montarQueryString()}`)
    resumo.value = data
  } catch { resumo.value = null } finally { loadingResumo.value = false }
}

async function carregarGrid () {
  loadingGrid.value = true
  try {
    const params = `page=${currentPage.value}&pageSize=25&sortBy=${sortBy.value || ''}&sortOrder=${sortOrder.value}&${montarQueryString()}`
    const { data, headers } = await api.get(`/SaldosEstoqueOrigem?${params}`)
    gridData.value = data
    const pag = headers['x-pagination'] ? JSON.parse(headers['x-pagination']) : null
    if (pag) totalPages.value = pag.totalPages
  } catch { gridData.value = [] } finally { loadingGrid.value = false }
}

async function carregarOpcoesDimensao () {
  const chaves = DIMENSOES.map(d => d.chave)
  for (const chave of chaves) loadingSlicers.value = { ...loadingSlicers.value, [chave]: true }
  try {
    const params = `dimensoes=${chaves.join(',')}&${montarQueryString()}`
    const { data } = await api.get(`/SaldosEstoqueOrigem/opcoes-dimensao/lote?${params}`)
    opcoesPorDimensao.value = data || {}
  } catch { opcoesPorDimensao.value = {} } finally {
    const novosLoading = {}
    for (const chave of chaves) novosLoading[chave] = false
    loadingSlicers.value = novosLoading
  }
}

function onFiltrosChange (novosFiltros) {
  filtros.value = novosFiltros
  currentPage.value = 1
  debounceCarregar()
}

function removerBadge (chave, id) {
  const ids = [...(filtros.value[chave] || [])]
  const idx = ids.indexOf(id)
  if (idx >= 0) ids.splice(idx, 1)
  onFiltrosChange({ ...filtros.value, [chave]: ids })
}

function resetarTudo () {
  filtros.value = {}
  filtrosAvancados.value = {}
  kpiAtivo.value = null
  sortBy.value = null
  sortOrder.value = 'asc'
  currentPage.value = 1
  carregarTudo()
}

function debounceCarregar () {
  clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => carregarTudo(), 150)
}

function onPageChange (page) {
  currentPage.value = page
  carregarGrid()
}

function onSortChange ({ sortBy: sb, sortOrder: so }) {
  sortBy.value = sb
  sortOrder.value = so
  carregarGrid()
}

function ordenarPorKPI (chave) {
  if (kpiAtivo.value === chave) {
    kpiAtivo.value = null
    sortBy.value = null
    sortOrder.value = 'asc'
  } else {
    kpiAtivo.value = chave
    const mapa = {
      custoMedio: { sortBy: 'custoUnitOrigemPadrao', sortOrder: 'desc' },
      valorBRL: { sortBy: 'custoUnitOrigemPadrao', sortOrder: 'desc' },
      valorUSD: { sortBy: 'custoUnitOrigemGerencial', sortOrder: 'desc' }
    }
    const ordem = mapa[chave]
    if (ordem) { sortBy.value = ordem.sortBy; sortOrder.value = ordem.sortOrder }
  }
  carregarGrid()
}

function aplicarFiltrosAvancados (filtrosA) {
  filtrosAvancados.value = filtrosA
  abrirFiltrosAvancados.value = false
  currentPage.value = 1
  carregarTudo()
}

function carregarPreset (filtrosP) {
  filtros.value = filtrosP
  currentPage.value = 1
  carregarTudo()
}

function exportarGrid () {
  if (adminTableRef.value?.exportar) adminTableRef.value.exportar()
}
</script>

<style scoped>
.dashboard-bi {
  --dashboard-bg: var(--bs-body-bg);
  --dashboard-border: var(--bs-border-color);
  --dashboard-header-bg: var(--bs-body-bg);
  --dashboard-kpi-bg: var(--bs-tertiary-bg);
  --dashboard-icon: var(--bs-primary);
  font-size: 0.875rem;
  background: var(--dashboard-bg);
}

.dashboard-header {
  min-height: 52px;
  z-index: 1020;
  background: var(--dashboard-header-bg);
  border-color: var(--dashboard-border) !important;
}

.dashboard-icon {
  color: var(--dashboard-icon);
}

.dashboard-subtitulo {
  color: var(--bs-secondary-color);
}

.dashboard-sidebar {
  flex-shrink: 0;
  overflow: hidden;
  border-color: var(--dashboard-border) !important;
}

.dashboard-main {
  min-width: 0;
}

.dashboard-kpis {
  background: var(--dashboard-kpi-bg);
  border-color: var(--dashboard-border) !important;
}

.dashboard-grid {
  background: var(--dashboard-bg);
}
</style>
